import type { Character, Scene } from '../types';
import { normalizeCharacterId } from './deriveBlockLinks';

export interface ClassifiedCharacter {
    name: string;
    isEnsemble: boolean;
    notes?: string;
    sceneIds: string[];
}

export interface ClassifiedScene {
    id: string;
    name: string;
    notes?: string;
    type?: 'scene' | 'song';
    songNumber?: string;
    parentSceneId?: string | null;
}

export function mapClassifiedCharacters(characters: readonly ClassifiedCharacter[] = []): Character[] {
    const usedIds = new Set<string>();

    return characters.map((character) => {
        const baseId = normalizeCharacterId(character.name);
        let id = baseId;
        let suffix = 2;
        while (usedIds.has(id)) {
            id = `${baseId}-${suffix}`;
            suffix += 1;
        }
        usedIds.add(id);

        return {
            id,
            name: character.name,
            isEnsemble: character.isEnsemble || false,
            notes: character.notes || '',
            sceneIds: character.sceneIds || [],
            parentId: null,
        };
    });
}

export function mapClassifiedScenes(scenes: readonly ClassifiedScene[] = []): Scene[] {
    return scenes.map(scene => ({
        ...scene,
        linkedSetId: null,
        notes: scene.notes || '',
        type: scene.type || 'scene',
        songNumber: scene.songNumber || undefined,
        parentSceneId: scene.parentSceneId ?? null,
    }));
}

export function mergeClassifiedCharacterDetails(
    currentCharacters: Character[],
    classifiedCharacters: readonly ClassifiedCharacter[] = []
): Character[] {
    const classifiedByName = new Map(
        classifiedCharacters.map(character => [
            normalizeCharacterId(character.name),
            character,
        ] as const)
    );
    let changed = false;

    const nextCharacters = currentCharacters.map(character => {
        if (character.deleted) return character;

        const classifiedCharacter = classifiedByName.get(normalizeCharacterId(character.name));
        if (!classifiedCharacter) return character;

        const hasSceneIds = (character.sceneIds || []).length > 0;
        const nextSceneIds = hasSceneIds
            ? character.sceneIds
            : classifiedCharacter.sceneIds || [];
        const shouldHydrateScenes = !hasSceneIds && nextSceneIds.length > 0;
        const shouldHydrateNotes = !character.notes && Boolean(classifiedCharacter.notes);

        if (!shouldHydrateScenes && !shouldHydrateNotes) return character;

        changed = true;
        return {
            ...character,
            ...(shouldHydrateScenes ? { sceneIds: nextSceneIds } : {}),
            ...(shouldHydrateNotes ? { notes: classifiedCharacter.notes } : {}),
        };
    });

    return changed ? nextCharacters : currentCharacters;
}
